/* Admin moderation panels — embedded inside AdminDashboard.jsx for now.
 *
 * Two sections:
 *   1. ProfessionalsModerationPanel — list/approve/suspend/feature/delete
 *      registered professionals across pending / verified / suspended.
 *   2. InquiriesModerationPanel — view inbound customer inquiries, assign
 *      to a professional, change status.
 *
 * Both use cookie-authenticated /api/admin/* endpoints (the admin auth
 * middleware enforces the cookie globally; we don't need to pass extra
 * headers).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShieldCheck,
  ShieldOff,
  Clock3,
  Star,
  Trash2,
  RefreshCw,
  Loader2,
  Inbox,
  Search,
  ChevronDown,
  Hammer,
} from "lucide-react";
import { CATEGORY_BY_KEY, PRO_CATEGORIES } from "@/professionals/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "verified", label: "Verified" },
  { key: "suspended", label: "Suspended" },
];

export const ProfessionalsModerationPanel = ({ onToast }) => {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/professionals?status=${tab}`);
      setItems(data.items || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load professionals", "error");
    } finally {
      setLoading(false);
    }
  }, [tab, onToast]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (pro, action) => {
    setBusyId(pro.id);
    try {
      const { data } = await axios.post(`${API}/admin/professionals/${pro.id}/${action}`);
      onToast?.(`${pro.full_name} → ${action}`, "ok");
      // Reload current tab — the pro may have moved out of it.
      await load();
      return data;
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Action failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (pro) => {
    if (!window.confirm(`Permanently delete ${pro.full_name}? This cannot be undone.`)) return;
    setBusyId(pro.id);
    try {
      await axios.delete(`${API}/admin/professionals/${pro.id}`);
      onToast?.(`Deleted ${pro.full_name}`, "ok");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Delete failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mt-6 rounded-3xl bg-white border-2 border-border overflow-hidden" data-testid="admin-pros-panel">
      <header className="bg-navy text-white px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Hammer className="h-5 w-5 text-gold" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold font-bold">Mitra Ecosystem</div>
            <h3 className="text-lg font-extrabold tracking-tight">Verify &amp; manage Mitras</h3>
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="admin-pros-refresh" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-bold border border-white/15">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="px-5 pt-4 flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-pros-tab-${t.key}`}
            aria-pressed={tab === t.key}
            className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-extrabold border-2 ${
              tab === t.key ? "bg-navy text-gold border-navy" : "bg-white text-navy border-border hover:border-navy"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-5 overflow-x-auto">
        {loading ? (
          <div className="inline-flex items-center gap-2 text-[12.5px] text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground" data-testid="admin-pros-empty">
            No {tab} Mitras.
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Mitra</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">City</th>
                <th className="py-2 pr-3">Exp</th>
                <th className="py-2 pr-3">Contact</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const meta = CATEGORY_BY_KEY[p.category];
                return (
                  <tr key={p.id} className="border-t border-border align-top" data-testid={`admin-pros-row-${p.id}`}>
                    <td className="py-3 pr-3">
                      <div className="font-extrabold text-navy">{p.full_name}</div>
                      <div className="text-[11.5px] text-muted-foreground line-clamp-2 max-w-xs">{p.bio}</div>
                      {p.featured && <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-gold/15 text-gold-dark border border-gold/40 text-[10px] font-bold uppercase tracking-wider"><Star className="h-2.5 w-2.5" /> Featured</span>}
                    </td>
                    <td className="py-3 pr-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: meta?.accent || "#0b2545" }}>
                        {meta?.label || p.category}
                      </span>
                    </td>
                    <td className="py-3 pr-3">{p.city}, {p.state}</td>
                    <td className="py-3 pr-3 font-bold">{p.experience_years}y</td>
                    <td className="py-3 pr-3">
                      <div className="font-mono">{p.phone}</div>
                      <div className="text-[11.5px] text-muted-foreground">{p.email}</div>
                    </td>
                    <td className="py-3 pr-3">
                      <div className="flex flex-wrap gap-1.5">
                        {p.status !== "verified" && (
                          <button type="button" disabled={busyId === p.id} onClick={() => moderate(p, "approve")} data-testid={`admin-pros-approve-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 text-white text-[11.5px] font-bold hover:bg-emerald-700 disabled:opacity-60">
                            <ShieldCheck className="h-3 w-3" /> Approve
                          </button>
                        )}
                        {p.status === "verified" && (
                          <button type="button" disabled={busyId === p.id} onClick={() => moderate(p, "suspend")} data-testid={`admin-pros-suspend-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-yellow-500 text-navy text-[11.5px] font-bold hover:bg-yellow-600 disabled:opacity-60">
                            <ShieldOff className="h-3 w-3" /> Suspend
                          </button>
                        )}
                        {p.status === "suspended" && (
                          <button type="button" disabled={busyId === p.id} onClick={() => moderate(p, "approve")} data-testid={`admin-pros-reapprove-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 text-white text-[11.5px] font-bold hover:bg-emerald-700 disabled:opacity-60">
                            <ShieldCheck className="h-3 w-3" /> Re-approve
                          </button>
                        )}
                        {p.status === "verified" && (
                          <button type="button" disabled={busyId === p.id} onClick={() => moderate(p, p.featured ? "unfeature" : "feature")} data-testid={`admin-pros-feature-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gold text-navy text-[11.5px] font-bold hover:bg-gold-dark hover:text-white disabled:opacity-60">
                            <Star className="h-3 w-3" /> {p.featured ? "Unfeature" : "Feature"}
                          </button>
                        )}
                        <button type="button" disabled={busyId === p.id} onClick={() => remove(p)} data-testid={`admin-pros-delete-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200 text-[11.5px] font-bold hover:bg-red-100 disabled:opacity-60">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

const INQUIRY_STATUSES = [
  "received",
  "assigned",
  "consultation_scheduled",
  "in_progress",
  "completed",
  "closed",
];

export const InquiriesModerationPanel = ({ onToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [pros, setPros] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries${statusFilter === "all" ? "" : `?status=${statusFilter}`}`),
        axios.get(`${API}/admin/professionals?status=verified`),
      ]);
      setItems(iRes.data.items || []);
      setPros(pRes.data.items || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load inquiries", "error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      (i.city || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.phone || "").includes(q)
    );
  }, [items, search]);

  const update = async (inq, patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/inquiries/${inq.id}`, patch);
      setItems((prev) => prev.map((x) => (x.id === inq.id ? data : x)));
      onToast?.(`Updated inquiry · ${data.status}`, "ok");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Update failed", "error");
    }
  };

  return (
    <section className="mt-6 rounded-3xl bg-white border-2 border-border overflow-hidden" data-testid="admin-inquiries-panel">
      <header className="bg-navy text-white px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Inbox className="h-5 w-5 text-gold" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold font-bold">Customer Inquiry Queue</div>
            <h3 className="text-lg font-extrabold tracking-tight">Custom design requests</h3>
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="admin-inquiries-refresh" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-bold border border-white/15">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="px-5 pt-4 flex flex-wrap gap-2 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-inquiries-status-filter"
          className="h-9 px-3 rounded-lg border-2 border-border bg-white text-[12.5px] font-bold text-navy outline-none focus:border-navy"
        >
          <option value="all">All statuses</option>
          {INQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <div className="relative flex-1 min-w-[160px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone or city"
            data-testid="admin-inquiries-search"
            className="w-full h-9 pl-9 pr-3 rounded-lg border-2 border-border bg-white text-[12.5px] outline-none focus:border-navy"
          />
        </div>
      </div>

      <div className="p-5 overflow-x-auto">
        {loading ? (
          <div className="inline-flex items-center gap-2 text-[12.5px] text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground" data-testid="admin-inquiries-empty">
            No inquiries yet. They'll appear here as soon as customers submit custom requests.
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Requirement</th>
                <th className="py-2 pr-3">Assigned to</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inq) => {
                const matchingPros = inq.category
                  ? pros.filter((p) => p.category === inq.category)
                  : pros;
                return (
                  <tr key={inq.id} className="border-t border-border align-top" data-testid={`admin-inquiry-row-${inq.id}`}>
                    <td className="py-3 pr-3">
                      <div className="font-extrabold text-navy">{inq.name}</div>
                      <div className="text-[11.5px] text-muted-foreground font-mono">+91 {inq.phone}</div>
                      <div className="text-[11.5px] text-muted-foreground">{inq.city}{inq.plot_size ? ` · ${inq.plot_size}` : ""}</div>
                    </td>
                    <td className="py-3 pr-3 max-w-md">
                      <div className="text-[12.5px] text-foreground line-clamp-3">{inq.description}</div>
                    </td>
                    <td className="py-3 pr-3 min-w-[180px]">
                      <select
                        value={inq.professional_id || ""}
                        onChange={(e) => update(inq, { professional_id: e.target.value || null })}
                        data-testid={`admin-inquiry-assign-${inq.id}`}
                        className="w-full h-9 px-2 rounded-lg border-2 border-border bg-white text-[12px] font-bold text-navy outline-none focus:border-navy"
                      >
                        <option value="">— Unassigned —</option>
                        {matchingPros.map((p) => (
                          <option key={p.id} value={p.id}>{p.full_name} · {p.city}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 pr-3 min-w-[170px]">
                      <select
                        value={inq.status}
                        onChange={(e) => update(inq, { status: e.target.value })}
                        data-testid={`admin-inquiry-status-${inq.id}`}
                        className="w-full h-9 px-2 rounded-lg border-2 border-border bg-white text-[12px] font-bold text-navy outline-none focus:border-navy"
                      >
                        {INQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </td>
                    <td className="py-3 pr-3 text-[11.5px] text-muted-foreground">
                      {new Date(inq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default ProfessionalsModerationPanel;
